import { useEffect, useImperativeHandle, useRef, useState, forwardRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Eraser, PenLine } from 'lucide-react';
import { instructorApi } from '../api/instructor';
import { InstructorLesson } from '../types';
import { Button } from './charte';
import { Sheet } from './shell/Sheet';

export interface SignaturePadHandle {
  isEmpty: () => boolean;
  toDataURL: () => string;
}

/** Zone de signature tactile réutilisable (doigt / souris). */
const SignaturePad = forwardRef<SignaturePadHandle, { label: string }>(
  function SignaturePad({ label }, ref) {
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const drawing = useRef(false);
    const dirty = useRef(false);
    const [, force] = useState(0);

    useEffect(() => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const ratio = window.devicePixelRatio || 1;
      // Taille de mise en page (offsetWidth) : la fenêtre s'ouvre avec une petite mise à l'échelle animée,
      // que getBoundingClientRect compterait et qui décalerait le trait.
      canvas.width = canvas.offsetWidth * ratio;
      canvas.height = canvas.offsetHeight * ratio;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.scale(ratio, ratio);
        ctx.lineWidth = 2.5;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        ctx.strokeStyle = '#0f172a';
      }
    }, []);

    useImperativeHandle(ref, () => ({
      isEmpty: () => !dirty.current,
      toDataURL: () => canvasRef.current?.toDataURL('image/png') ?? '',
    }));

    const point = (e: React.PointerEvent<HTMLCanvasElement>) => {
      const rect = e.currentTarget.getBoundingClientRect();
      return { x: e.clientX - rect.left, y: e.clientY - rect.top };
    };
    const start = (e: React.PointerEvent<HTMLCanvasElement>) => {
      e.preventDefault();
      const ctx = canvasRef.current?.getContext('2d');
      if (!ctx) return;
      drawing.current = true;
      const { x, y } = point(e);
      ctx.beginPath();
      ctx.moveTo(x, y);
      e.currentTarget.setPointerCapture(e.pointerId);
    };
    const move = (e: React.PointerEvent<HTMLCanvasElement>) => {
      if (!drawing.current) return;
      e.preventDefault();
      const ctx = canvasRef.current?.getContext('2d');
      if (!ctx) return;
      const { x, y } = point(e);
      ctx.lineTo(x, y);
      ctx.stroke();
      if (!dirty.current) {
        dirty.current = true;
        force((n) => n + 1);
      }
    };
    const end = () => {
      drawing.current = false;
    };
    const clear = () => {
      const canvas = canvasRef.current;
      const ctx = canvas?.getContext('2d');
      if (canvas && ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);
      dirty.current = false;
      force((n) => n + 1);
    };

    return (
      <div>
        <div className="flex items-center justify-between mb-1">
          <span className="text-[12.5px] font-medium text-secondary">{label}</span>
          <button
            type="button"
            onClick={clear}
            disabled={!dirty.current}
            className="inline-flex min-h-[32px] items-center gap-1 rounded-pill px-2 text-xs font-medium text-secondary hover:bg-surface-2 hover:text-body disabled:opacity-40"
          >
            <Eraser size={13} />
            Effacer
          </button>
        </div>
        <div className="rounded-row border-2 border-dashed border-primary-200 bg-surface-light">
          <canvas
            ref={canvasRef}
            onPointerDown={start}
            onPointerMove={move}
            onPointerUp={end}
            onPointerLeave={end}
            className="h-40 w-full cursor-crosshair touch-none rounded-row sm:h-44"
          />
        </div>
      </div>
    );
  },
);

/**
 * Émargement : l'élève signe sa présence, le moniteur peut contre-signer pour
 * valider la séance. Les signatures (PNG) sont envoyées au backend.
 */
export default function AttendanceModal({
  lesson,
  onClose,
}: {
  lesson: InstructorLesson;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const studentPad = useRef<SignaturePadHandle>(null);
  const instructorPad = useRef<SignaturePadHandle>(null);
  const [error, setError] = useState<string | null>(null);
  const candidate = lesson.candidate;
  const candidateName = candidate
    ? `${candidate.firstName} ${candidate.lastName}`
    : 'Élève';

  const mutation = useMutation({
    mutationFn: () => {
      const signature = studentPad.current?.toDataURL() ?? '';
      const instructorSignature =
        instructorPad.current && !instructorPad.current.isEmpty()
          ? instructorPad.current.toDataURL()
          : undefined;
      return instructorApi.signAttendance(lesson.id, signature, instructorSignature);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['today'] });
      void queryClient.invalidateQueries({ queryKey: ['planning'] });
      void queryClient.invalidateQueries({ queryKey: ['students'] });
      onClose();
    },
    onError: (e: Error) => setError(e.message || 'Émargement impossible.'),
  });

  const submit = () => {
    if (studentPad.current?.isEmpty()) {
      setError("La signature de l'élève est requise.");
      return;
    }
    setError(null);
    mutation.mutate();
  };

  return (
    <Sheet
      title={
        <span className="flex items-center gap-2">
          <PenLine size={18} className="text-primary" aria-hidden />
          Émargement
        </span>
      }
      description={`${candidateName} atteste sa présence à la leçon`}
      onClose={onClose}
      className="sm:max-w-xl"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Annuler
          </Button>
          <Button variant="primary" className="ml-auto" loading={mutation.isPending} onClick={submit}>
            Valider l'émargement
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <SignaturePad ref={studentPad} label={`Signature de l'élève (${candidateName})`} />
        <SignaturePad ref={instructorPad} label="Contre-signature du moniteur (optionnel)" />
        {error && (
          <p role="alert" className="rounded-xl bg-danger-light px-3 py-2 text-sm text-danger-dark">
            {error}
          </p>
        )}
      </div>
    </Sheet>
  );
}
